import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  Activity,
} from 'lucide-react';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/hooks/use-toast';

const ACTION_LABELS = {
  login: { label: 'Giriş', color: 'bg-blue-100 text-blue-800' },
  logout: { label: 'Çıkış', color: 'bg-gray-100 text-gray-800' },
  view_property: { label: 'İlan Görüntüleme', color: 'bg-cyan-100 text-cyan-800' },
  add_favorite: { label: 'Favori Ekleme', color: 'bg-pink-100 text-pink-800' },
  remove_favorite: { label: 'Favori Silme', color: 'bg-red-100 text-red-800' },
  create_investment: { label: 'Yatırım Oluşturma', color: 'bg-green-100 text-green-800' },
  update_investment: { label: 'Yatırım Güncelleme', color: 'bg-yellow-100 text-yellow-800' },
  upload_document: { label: 'Belge Yükleme', color: 'bg-indigo-100 text-indigo-800' },
  delete_document: { label: 'Belge Silme', color: 'bg-red-100 text-red-800' },
  update_profile: { label: 'Profil Güncelleme', color: 'bg-purple-100 text-purple-800' },
  create_transaction: { label: 'İşlem Oluşturma', color: 'bg-emerald-100 text-emerald-800' },
};

const ENTITY_LABELS = {
  investment: 'Yatırım',
  document: 'Belge',
  favorite: 'Favori',
  profile: 'Profil',
  property_view: 'İlan Görüntüleme',
  transaction: 'İşlem',
};

const PAGE_SIZE = 30;

export default function AdminActivitiesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data, count, error } = await adminService.getActivityLogs({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    if (error) {
      toast({ title: 'Hata', description: 'Aktiviteler yüklenemedi', variant: 'destructive' });
    } else {
      setLogs(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }, [page, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '-';

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Activity className="w-5 h-5" />
              <span className="text-sm">Son kullanıcı aktiviteleri (user_activity_logs tablosu)</span>
            </div>
            <Button variant="outline" size="icon" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Aktivite Logları ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Henüz aktivite kaydı bulunamadı</p>
              <p className="text-xs mt-1">Kullanıcılar aksiyonlarda bulundukça bu tablo dolacaktır.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>Aksiyon</TableHead>
                      <TableHead>Varlık Tipi</TableHead>
                      <TableHead>Varlık ID</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="text-right">Detay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => {
                      const actionCfg = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-800' };
                      return (
                        <TableRow key={log.id}>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/admin/users/${log.user_id}`)}
                              className="text-blue-600 hover:underline text-xs font-mono"
                            >
                              {log.user_id?.slice(0, 8)}...
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${actionCfg.color} text-xs`}>{actionCfg.label}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{ENTITY_LABELS[log.entity_type] || log.entity_type || '-'}</TableCell>
                          <TableCell className="text-xs font-mono max-w-[100px] truncate">
                            {log.entity_id?.slice(0, 8) || '-'}
                          </TableCell>
                          <TableCell className="text-xs">{log.ip_address || '-'}</TableCell>
                          <TableCell className="text-xs">{formatDate(log.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${log.user_id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">{page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} / {totalCount}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm">{page + 1} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <Toaster />
    </div>
  );
}
